import React, { useState, useEffect, useMemo } from 'react';
import { useLoaderData, useParams, useSearchParams, Link } from 'react-router-dom';
import { Message } from '../../components/PrimereactComponents';
import { useTranslation } from 'react-i18next';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import StatChip from '../../components/common/StatChip/StatChip';
import { MeetingCard } from '../../components/meetings/MeetingCard';
import YearStrip, { YearGroup } from '../../components/meetings/YearStrip';
import VoteSplit from '../../components/common/VoteSplit/VoteSplit';
import type { DropdownYearOptions, SessionData } from '../../api';
import { URLKeys } from '../../Constants';
import { useAppContext } from '../../context/AppContext';
import { useLocalizationLabels } from '../../context/LocalizationLabelContext';
import { useFormatters } from '../../utils/texts';
import { useCouncilPath } from '../../hooks/useCouncilPath';

function Meetings() {
    const { t, i18n } = useTranslation('meetings');
    const [searchParams, setSearchParams] = useSearchParams();
    const { bodyType } = useParams();
    const { basicData, municipalityData: municipality } = useAppContext();
    const {organOptionLabels} = useLocalizationLabels();
    const formatter = useFormatters(i18n.language);
    const councilPath = useCouncilPath();

    const {
        meetingsData: sessionsData,
        dropdownYearOptions
    } = useLoaderData() as {
        meetingsData: SessionData[];
        dropdownYearOptions: DropdownYearOptions[];
    };

    // Full set of sort options, used to resolve state from the URL and by the quick-sort toggles.
    const allSortOptions = [
        { name: t('sortByDateAsc'), code: 'datum-vzest' },
        { name: t('sortByDateDesc'), code: 'datum-sest' },
        { name: t('sortByVotesAsc'), code: 'pocet_platnych_hlasovani-vzest' },
        { name: t('sortByVotesDesc'), code: 'pocet_platnych_hlasovani-sest' },
        { name: t('sortByAttendanceAsc'), code: 'ucast-vzest' },
        { name: t('sortByAttendanceDesc'), code: 'ucast-sest' },
        { name: t('sortByContentiousAsc'), code: 'pocet_tesnych_hlasovani-vzest' },
        { name: t('sortByContentiousDesc'), code: 'pocet_tesnych_hlasovani-sest' }
    ];

    const quickSortButtons: {
        key: string;
        icon: string;
        shortLabel: string;
        ascLabel: string;
        descLabel: string;
    }[] = [
        { key: 'datum', icon: 'calendar_today', shortLabel: t('sortByDateShort'), ascLabel: t('sortByDateAsc'), descLabel: t('sortByDateDesc') },
        { key: 'pocet_platnych_hlasovani', icon: 'stacks', shortLabel: t('sortByVotesShort'), ascLabel: t('sortByVotesAsc'), descLabel: t('sortByVotesDesc') },
        { key: 'ucast', icon: 'group', shortLabel: t('sortByAttendanceShort'), ascLabel: t('sortByAttendanceAsc'), descLabel: t('sortByAttendanceDesc') },
        { key: 'pocet_tesnych_hlasovani', icon: 'warning', shortLabel: t('sortByContentiousShort'), ascLabel: t('sortByContentiousAsc'), descLabel: t('sortByContentiousDesc') }
    ];

    const selectedYearParam = searchParams.get(URLKeys.selectedYear);
    const selectedYearNumber = selectedYearParam ? Number(selectedYearParam) : null;

    const getYearOption = (year: number | null) => {
        return dropdownYearOptions.find((option) => option.id === year) || undefined;
    };

    const [filteringState, setFilteringState] = useState({
        selectedYear: getYearOption(selectedYearNumber),
        sortBy: allSortOptions.find((option) => option.code === searchParams.get(URLKeys.sortBy)) || allSortOptions[0]
    });

    const handleFilteringChange = (value: any, key: keyof typeof filteringState) => {
        const newFilteringState = { ...filteringState, [key]: value };
        setFilteringState(newFilteringState);
        setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set(URLKeys.sortBy, newFilteringState.sortBy.code);
            if (newFilteringState.selectedYear) {
                newParams.set(URLKeys.selectedYear, String(newFilteringState.selectedYear.id));
            } else {
                newParams.delete(URLKeys.selectedYear);
            }
            return newParams;
        });
    };

    const [activeSortKey, activeSortOrder] = filteringState.sortBy.code.split('-');

    const toggleSort = (key: string, defaultOrder: 'vzest' | 'sest') => {
        const nextOrder = activeSortKey === key ? (activeSortOrder === 'vzest' ? 'sest' : 'vzest') : defaultOrder;
        const nextOption = allSortOptions.find((option) => option.code === `${key}-${nextOrder}`);
        if (nextOption) handleFilteringChange(nextOption, 'sortBy');
    };

    const sortSessionsData = (data: SessionData[], sortBy: { code: string }) => {
        if (!sortBy) return data;

        let [key, order] = sortBy.code.split('-');
        if (key === 'datum') key = 'datum_od';
        return [...data].sort((a, b) => {
            if (key === 'datum_od') {
                const dateA = new Date(a[key].split('.').reverse().join('-'));
                const dateB = new Date(b[key].split('.').reverse().join('-'));
                return order === 'vzest' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
            } else {
                return order === 'vzest'
                    ? (a[key as keyof SessionData] as number) -
                          (b[key as keyof SessionData] as number)
                    : (b[key as keyof SessionData] as number) -
                          (a[key as keyof SessionData] as number);
            }
        });
    };

    const meetingStats = useMemo(() => {
        const meetings = sessionsData ?? [];
        if (meetings.length === 0) {
            return {
                avgAttendance: 0,
                totalValidVotes: 0,
                totalAccepted: 0,
                totalRejected: 0,
                frequencyDays: null as number | null,
                mostContentious: undefined as SessionData | undefined,
                yearGroups: [] as YearGroup[]
            };
        }

        const totalValidVotes = meetings.reduce((sum, m) => sum + m.pocet_platnych_hlasovani, 0);
        const totalAccepted = meetings.reduce((sum, m) => sum + m.pocet_prijatych_hlasovani, 0);
        const totalRejected = meetings.reduce((sum, m) => sum + m.pocet_zamitnutych_hlasovani, 0);
        const avgAttendance = meetings.reduce((sum, m) => sum + m.dochazka, 0) / meetings.length;

        const sortedByDate = [...meetings].sort(
            (a, b) => new Date(a.datum_od).getTime() - new Date(b.datum_od).getTime()
        );

        let frequencyDays: number | null = null;
        if (sortedByDate.length > 1) {
            const first = new Date(sortedByDate[0].datum_od).getTime();
            const last = new Date(sortedByDate[sortedByDate.length - 1].datum_od).getTime();
            frequencyDays = Math.round((last - first) / (sortedByDate.length - 1) / (1000 * 60 * 60 * 24));
        }

        const mostContentious = meetings.reduce<SessionData | undefined>((best, m) => {
            if (m.pocet_tesnych_hlasovani <= 0) return best;
            if (!best) return m;
            if (m.pocet_tesnych_hlasovani > best.pocet_tesnych_hlasovani) return m;
            if (
                m.pocet_tesnych_hlasovani === best.pocet_tesnych_hlasovani &&
                new Date(m.datum_od).getTime() > new Date(best.datum_od).getTime()
            ) {
                return m;
            }
            return best;
        }, undefined);

        const yearMap = new Map<number, { count: number; attendanceSum: number }>();
        sortedByDate.forEach((m) => {
            const year = new Date(m.datum_od).getFullYear();
            const entry = yearMap.get(year) ?? { count: 0, attendanceSum: 0 };
            entry.count += 1;
            entry.attendanceSum += m.dochazka;
            yearMap.set(year, entry);
        });

        const years = Array.from(yearMap.keys()).sort((a, b) => a - b);
        let previousAvg: number | null = null;
        const yearGroups: YearGroup[] = years.map((year) => {
            const { count, attendanceSum } = yearMap.get(year)!;
            const yearAvgAttendance = attendanceSum / count;
            const delta = previousAvg === null ? null : Math.round(yearAvgAttendance - previousAvg);
            previousAvg = yearAvgAttendance;
            return { year, count, avgAttendance: yearAvgAttendance, delta };
        });

        return { avgAttendance, totalValidVotes, totalAccepted, totalRejected, frequencyDays, mostContentious, yearGroups };
    }, [sessionsData]);

    useEffect(() => {
        setSearchParams(
            (prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set(URLKeys.sortBy, filteringState.sortBy.code);
                if (
                    filteringState.selectedYear &&
                    prevParams.get(URLKeys.selectedYear) !== String(filteringState.selectedYear.id)
                ) {
                    newParams.set(URLKeys.selectedYear, String(filteringState.selectedYear.id));
                } else if (!filteringState.selectedYear) {
                    newParams.delete(URLKeys.selectedYear);
                }
                return newParams;
            },
            { replace: true }
        );
        // searchParams/setSearchParams are intentionally excluded: both change identity on every
        // location.search update, so including them here makes this effect keep re-firing after its
        // own navigate() call - a stale re-fire can then land after the user has already navigated
        // away (e.g. into a meeting detail page) and snap the URL back to this list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteringState.selectedYear, filteringState.sortBy.code]);

    if (sessionsData !== undefined && municipality !== undefined && dropdownYearOptions !== undefined) {
        const sortedSessionsData = sortSessionsData(sessionsData, filteringState.sortBy);

        const subtitle = `${t('inTerm', {
            ns: 'meetings',
            num: sessionsData.length,
            where: bodyType ? municipality.organy[bodyType].misto : '',
            type: bodyType ? organOptionLabels[bodyType]?.label_genitiv ?? bodyType : ''
        })}`;


        return (
            <div className='page-dashboard'>
                <div className='page-header-row'>
                    <ContentSection
                        title={t('meetings')}
                        level={1}
                        subtitle={basicData.funkcni_obdobi}
                        className='page-title-section'
                    />
                    <div className='stat-chip-row'>
                        <StatChip icon='event_available' value={sessionsData.length} label={t('meetings')} />
                        <StatChip
                            icon='group'
                            value={formatter.percent2From100(meetingStats.avgAttendance)}
                            label={t('avgAttendance')}
                        />
                        {meetingStats.frequencyDays !== null && (
                            <StatChip icon='event_repeat' value={meetingStats.frequencyDays} label={t('meetingFrequency')} />
                        )}
                    </div>
                </div>
                <div className='page-description meetings-summary'>
                    {subtitle}
                    {meetingStats.totalValidVotes > 0 && (
                        <>
                            {' '}
                            {t('votesSummaryLead', { votes: meetingStats.totalValidVotes })}{' '}
                            <VoteSplit accepted={meetingStats.totalAccepted} rejected={meetingStats.totalRejected} />
                        </>
                    )}
                    {meetingStats.mostContentious && (
                        <>
                            {' '}
                            {t('mostContentiousLead')}{' '}
                            <Link
                                to={councilPath('zasedani', meetingStats.mostContentious.id)}
                                title={t('closeVotesTooltip')}
                            >
                                {t('meetingNo', { num: meetingStats.mostContentious.cislo_zasedani })}
                            </Link>{' '}
                            {t('mostContentiousTrail', { count: meetingStats.mostContentious.pocet_tesnych_hlasovani })}
                        </>
                    )}
                </div>
                {meetingStats.yearGroups.length > 0 && (
                    <div className='year-and-sort-row'>
                        <YearStrip
                            years={meetingStats.yearGroups}
                            selectedYear={filteringState.selectedYear?.id}
                            onSelect={(year) => handleFilteringChange(getYearOption(year ?? null), 'selectedYear')}
                            formatAttendance={formatter.percentFrom100}
                            meetingsLabel={t('meetings').toLowerCase()}
                            ariaLabel={t('selectYearMeeting')}
                            pillTitle={(year) => t('yearPillTooltip', { year })}
                        />
                        <div className='quick-sort-row'>
                            {quickSortButtons.map((btn) => {
                                const isActive = activeSortKey === btn.key;
                                return (
                                    <button
                                        key={btn.key}
                                        type='button'
                                        className={`quick-sort-toggle${isActive ? ' is-active' : ''}`}
                                        onClick={() => toggleSort(btn.key, 'sest')}
                                        title={isActive && activeSortOrder === 'vzest' ? btn.ascLabel : btn.descLabel}
                                    >
                                        <span className='material-symbols-rounded'>{btn.icon}</span>
                                        {btn.shortLabel}
                                        {isActive && (
                                            <span className='quick-sort-arrow'>
                                                {activeSortOrder === 'vzest' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
                <div className='card-list'>
                    {sortedSessionsData.length === 0 ? (
                        <Message severity='info' text={t('noDataFound')} />
                    ) : (
                        sortedSessionsData.map((meeting: SessionData, i: number) => {
                            if (
                                !filteringState.selectedYear ||
                                meeting.datum_od.includes(String(filteringState.selectedYear.id))
                            ) {
                                return <MeetingCard meetingData={meeting} key={i} />;
                            }
                            return null;
                        })
                    )}
                </div>
            </div>
        );
    }
}

export default Meetings;
